import { Link } from 'react-router-dom';
import { Icon } from '../Icon';
import { Loading } from '../Ui';
import { encodePath } from '../../shared/utils/format';
import type { Revision } from '../../shared/types/api';
import { useState } from 'react';

export function WikiPageTree({
  revision,
  base,
  rev,
  path,
  unavailable,
  loading = false,
}: {
  revision?: Revision;
  base: string;
  rev: string | null;
  path: string;
  unavailable: boolean;
  loading?: boolean;
}) {
  const [expanded, setExpanded] = useState(
    () => typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width:760px)').matches,
  );
  const groups = new Map<string, string[]>();
  revision?.manifest
    ?.filter((entry) => /\.md$/i.test(entry.path))
    .forEach((entry) => {
      const folder = entry.path.split('/').slice(0, -1).join('/') || 'Root';
      groups.set(folder, [...(groups.get(folder) ?? []), entry.path]);
    });
  return (
    <details
      className="wiki-page-tree"
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary>
        <Icon name="book" />
        Knowledge
        <span>{revision?.manifest?.filter((entry) => /\.md$/i.test(entry.path)).length ?? ''}</span>
      </summary>
      <nav aria-label="Wiki pages">
        {unavailable ? (
          <p className="hint">Could not load the page tree. Retry the wiki request.</p>
        ) : loading && !revision ? (
          <Loading />
        ) : !groups.size ? (
          <p className="hint">No synced Markdown pages yet.</p>
        ) : (
          [...groups]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([folder, files]) => (
              <details key={folder} open>
                <summary>
                  <Icon name="folder" />
                  <span>{folder.replace(/^wiki\/?/, '') || 'Wiki pages'}</span>
                  <small>{files.length}</small>
                </summary>
                <ul>
                  {files.sort().map((file) => (
                    <li key={file}>
                      <Link
                        to={`${base}/blob/${encodePath(file)}${rev ? `?rev=${encodeURIComponent(rev)}` : ''}`}
                        aria-current={path === file ? 'page' : undefined}
                      >
                        <Icon name="file" />
                        <span>{file.split('/').pop()?.replace(/\.md$/i, '').replace(/[-_]/g, ' ')}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </details>
            ))
        )}
      </nav>
    </details>
  );
}
